import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  TextField,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import marketingService from '../services/marketingService';
import SocialPostComposer from '../components/SocialPostComposer';
import SocialPreview from '../components/SocialPreview';
import SchedulePicker from '../components/SchedulePicker';

export default function SocialMedia() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuPost, setMenuPost] = useState(null);
  const [platformFilter, setPlatformFilter] = useState('');
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadPosts();
  }, [platformFilter]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (platformFilter) filters.platform = platformFilter;

      const data = await marketingService.getSocialPosts(filters);
      setPosts(data.posts);
    } catch (error) {
      showAlert('Failed to load posts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenComposer = (post = null) => {
    setSelectedPost(post);
    setComposerOpen(true);
    handleCloseMenu();
  };

  const handleCloseComposer = () => {
    setComposerOpen(false);
    setSelectedPost(null);
  };

  const handleSavePost = async (postData) => {
    try {
      if (selectedPost) {
        await marketingService.updateSocialPost(selectedPost.id, postData);
        showAlert('Post updated successfully');
      } else {
        await marketingService.createSocialPost(postData);
        showAlert('Post created successfully');
      }
      handleCloseComposer();
      loadPosts();
    } catch (error) {
      showAlert('Failed to save post', 'error');
    }
  };

  const handlePreview = (post) => {
    setSelectedPost(post);
    setPreviewOpen(true);
    handleCloseMenu();
  };

  const handleSchedule = (post) => {
    setSelectedPost(post);
    setScheduleOpen(true);
    handleCloseMenu();
  };

  const handleConfirmSchedule = async (scheduledAt) => {
    try {
      await marketingService.scheduleSocialPost(selectedPost.id, scheduledAt);
      showAlert('Post scheduled successfully');
      setScheduleOpen(false);
      loadPosts();
    } catch (error) {
      showAlert('Failed to schedule post', 'error');
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm('Delete this post?')) return;

    try {
      await marketingService.deleteSocialPost(post.id);
      showAlert('Post deleted successfully');
      loadPosts();
    } catch (error) {
      showAlert('Failed to delete post', 'error');
    }
    handleCloseMenu();
  };

  const handleMenuOpen = (event, post) => {
    setAnchorEl(event.currentTarget);
    setMenuPost(post);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuPost(null);
  };

  const showAlert = (message, severity = 'success') => {
    setAlert({ open: true, message, severity });
    setTimeout(() => setAlert({ ...alert, open: false }), 3000);
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'facebook': return <FacebookIcon />;
      case 'instagram': return <InstagramIcon />;
      case 'twitter': return <TwitterIcon />;
      default: return null;
    }
  };

  const getPlatformColor = (platform) => {
    switch (platform) {
      case 'facebook': return '#1877F2';
      case 'instagram': return '#E4405F';
      case 'twitter': return '#1DA1F2';
      default: return '#666';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'posted': return 'success';
      case 'scheduled': return 'info';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box>
      {alert.open && (
        <Alert severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            label="Platform"
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
          >
            <MenuItem value="">All Platforms</MenuItem>
            <MenuItem value="facebook">Facebook</MenuItem>
            <MenuItem value="instagram">Instagram</MenuItem>
            <MenuItem value="twitter">Twitter</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenComposer()}
            sx={{ height: '56px' }}
          >
            New Post
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {posts.map((post) => (
          <Grid item xs={12} sm={6} md={4} key={post.id}>
            <Card>
              {post.image_url && (
                <CardMedia
                  component="img"
                  height="200"
                  image={post.image_url}
                  alt="Post image"
                />
              )}
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      color: getPlatformColor(post.platform),
                      display: 'flex',
                      alignItems: 'center',
                      mr: 2
                    }}
                  >
                    {getPlatformIcon(post.platform)}
                  </Box>
                  <Chip
                    label={post.status}
                    color={getStatusColor(post.status)}
                    size="small"
                  />
                  <IconButton
                    sx={{ ml: 'auto' }}
                    onClick={(e) => handleMenuOpen(e, post)}
                  >
                    <MoreIcon />
                  </IconButton>
                </Box>

                <Typography variant="body2" color="text.secondary">
                  {post.content.substring(0, 100)}
                  {post.content.length > 100 && '...'}
                </Typography>

                {post.sale_title && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Sale: {post.sale_title}
                  </Typography>
                )}

                {post.scheduled_at && (
                  <Typography variant="caption" color="info.main" sx={{ mt: 1, display: 'block' }}>
                    Scheduled: {new Date(post.scheduled_at).toLocaleString()}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => handlePreview(menuPost)}>
          Preview
        </MenuItem>
        {menuPost?.status === 'draft' && (
          <MenuItem onClick={() => handleOpenComposer(menuPost)}>
            <EditIcon sx={{ mr: 1 }} /> Edit
          </MenuItem>
        )}
        {menuPost?.status === 'draft' && (
          <MenuItem onClick={() => handleSchedule(menuPost)}>
            <ScheduleIcon sx={{ mr: 1 }} /> Schedule
          </MenuItem>
        )}
        {menuPost?.status !== 'posted' && (
          <MenuItem onClick={() => handleDelete(menuPost)}>
            <DeleteIcon sx={{ mr: 1 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={composerOpen}
        onClose={handleCloseComposer}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedPost ? 'Edit Post' : 'New Post'}
        </DialogTitle>
        <DialogContent>
          <SocialPostComposer
            post={selectedPost}
            onSave={handleSavePost}
            onCancel={handleCloseComposer}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Post Preview</DialogTitle>
        <DialogContent>
          {selectedPost && <SocialPreview post={selectedPost} />}
        </DialogContent>
      </Dialog>

      <Dialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Schedule Post</DialogTitle>
        <DialogContent>
          <SchedulePicker
            onConfirm={handleConfirmSchedule}
            onCancel={() => setScheduleOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
